import {booleanAttribute, Component, effect, input, Input, OnInit, signal} from '@angular/core';
import {DialogModule} from "primeng/dialog";
import {ButtonModule} from "primeng/button";
import {CurrencyService} from "../../../services/currency.service";
import {DropdownModule} from "primeng/dropdown";
import {InputNumberModule} from "primeng/inputnumber";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {CartService} from "../../../services/cart.service";
import {ToastService} from "../../../services/toast.service";
import {ToastMessage} from "../../../models/toastMessage";
import {DynamicCurrencyPipe} from "../../../pipes/dynamic-currency.pipe";
import {AsyncPipe, DatePipe, DecimalPipe, JsonPipe} from "@angular/common";
import {firstValueFrom} from "rxjs";
import {MultiSelectModule} from "primeng/multiselect";
import {ConditionnementService} from "../../../services/conditionnement.service";
import {Conditionnement, ConditionnementCart} from "../../../models/Conditionnements/Conditionnement";
import {CampagneProduct} from "../../../models/Campagne/CampagneProduct";
import {ProductCart} from "../../../models/Cart/ProductCart";
import {ResponsiveService} from "../../../services/responsive.service";
import {StatutStockPipe} from "../../../pipes/statut-stock.pipe";
import {TypeCampagne} from "../../../models/Campagne/TypeCampagne";
import {CentilisationPipe} from "../../../pipes/centilisation.pipe";
import {Tarif} from "../../../models/Tarif/TarifRecent";
import {SetMaxCartPipe} from "../../../pipes/set-max-cart.pipe";
import {toSignal} from "@angular/core/rxjs-interop";
import {UserService} from "../../../services/user.service";
import Decimal from 'decimal.js';

@Component({
  selector: 'app-add-to-cart',
  standalone: true,
  imports: [
    DialogModule,
    ButtonModule,
    DropdownModule,
    InputNumberModule,
    ReactiveFormsModule,
    DynamicCurrencyPipe,
    DecimalPipe,
    MultiSelectModule,
    AsyncPipe,
    DatePipe,
    StatutStockPipe,
    JsonPipe,
    CentilisationPipe,
  ],
  providers: [SetMaxCartPipe],
  templateUrl: './add-to-cart.component.html',
  styleUrl: './add-to-cart.component.scss'
})
export class AddToCartComponent implements OnInit {
  @Input({transform: booleanAttribute}) show: boolean = false;
  @Input() product!: CampagneProduct | Tarif | ProductCart;

  cartForm!: FormGroup;
  conditionnements = signal<ConditionnementCart[]>([]);
  formats = signal<any | undefined>(undefined);
  qty = signal<number | null>(null);
  protected readonly TypeCampagne = TypeCampagne;

  s_isPageVin = input<boolean>();
  s_campagneProduct = signal<CampagneProduct | null>(null);
  s_tarif = signal<Tarif | null>(null);
  s_productCart = signal<ProductCart | null>(null);
  s_max = signal<number | undefined>(undefined);
  s_labelSave = signal('');

  s_me = toSignal(this.userService.me$);

  isConditionOpen: boolean = false;
  isQteOpen: boolean = false;
  constructor(
    protected currencyService: CurrencyService,
    private formBuilder: FormBuilder,
    protected cartService: CartService,
    protected condiService: ConditionnementService,
    protected responsiveService: ResponsiveService,
    private toastService: ToastService,
    private setMaxCart: SetMaxCartPipe,
    private userService: UserService
  ) {

    effect(() => {
      if(!this.s_me())
        this.userService.getMe();
    });
  }

  async ngOnInit() {
    this.cartForm = this.formBuilder.group({
      conditionnement: [null, Validators.required],
      centilisation: [null, Validators.required],
      nbBouteilles: [null],
      quantity: [1, Validators.required],
    })

    if (this.isCampagne(this.product)) {
      this.s_campagneProduct.set(this.product);
      this.s_max.set(this.setMaxCart.transform(this.s_campagneProduct()!))
    }
    else if (this.isTarif(this.product)) {
      this.s_tarif.set(this.product)
    }
    else {
      this.s_productCart.set(this.product);
      this.s_max.set(this.s_productCart()?.maxQty)
    }
    if (this.cartService.type() && this.cartService.type() === TypeCampagne.Primeurs) {
      const conditionnementsForCart = await firstValueFrom(this.condiService.getConditionnementsForCart(this.product.idVin!, this.product.millesime!));
      this.setConditionnement((this.s_campagneProduct() ? this.s_campagneProduct() : this.s_productCart()), conditionnementsForCart)
    }

    if (this.cartService.editMode()) {
      this.cartForm.controls['conditionnement'].setValue(this.cartService.formData()?.conditionnement);
      this.cartForm.controls['nbBouteilles'].setValue(this.s_campagneProduct()?.nbBouteilles);
      if (this.conditionnements()?.length){
        this.setFormat();
      }
      this.cartForm.controls['centilisation'].setValue(this.cartService.formData()?.centilisation);
      this.cartForm.controls['quantity'].setValue(this.cartService.formData()?.quantity);
    } else {
      if (this.s_campagneProduct()) {
        this.cartForm.controls['conditionnement'].setValue(this.s_campagneProduct()?.conditionnementCampagne);
        this.cartForm.controls['nbBouteilles'].setValue(this.s_campagneProduct()?.nbBouteilles);
        this.setFormat();
      } else {
        this.cartForm.controls['conditionnement'].setValue(this.s_tarif()?.code_Caisse! + this.s_tarif()?.nb_Bouteille_Caisse);
        this.cartForm.controls['nbBouteilles'].setValue(this.s_tarif()?.nb_Bouteille_Caisse);
      }
      this.cartForm.controls['centilisation'].setValue(this.product.centilisation);
    }

    this.setQuantity();
    if (this.cartService.editMode()) {
      this.s_labelSave.set($localize `:@@modifier: Modifier`)
    } else {
      this.s_labelSave.set($localize `:@@ajouter: Ajouter`)
    }
  }

  setConditionnement(product: CampagneProduct | ProductCart | null, conditionnementsForCart: ConditionnementCart[]) {
    conditionnementsForCart.map((c: Conditionnement) => c.label = c.code_Caisse + c.nbBouteilles!.toString());

    if (conditionnementsForCart) {
      // On regroupe tous les centilisations par conditionnement via les suppléments vin.
      conditionnementsForCart.forEach((c: Conditionnement) => {
        if (this.conditionnements().some((condi: ConditionnementCart) => condi.label === c.label)) {
          this.conditionnements().map((condi: ConditionnementCart) => {
            if (condi.label === c.label) {
              condi.centilisations?.push({value: c.centilisation, idCondi: c.idCondi})
            }
          });
        } else {
          this.conditionnements().push({
            label: c.label,
            centilisations: [{value: c.centilisation, idCondi: c.idCondi}],
            nbBouteilles: c.nbBouteilles
          });
        }
      })

      // Soit on ajoute le conditionnement du produit avec ses centilisations, soit on l'ajoute à l'existant
      if (!this.conditionnements().some(c => c.label === product?.conditionnementCampagne)) {
        this.conditionnements().push(
          {
            label: product?.conditionnementCampagne,
            centilisations: [{value: product?.centilisation!, idCondi: product?.conditionnementCampagne}]
          })
      } else {
        this.conditionnements().find(c => {
          if (c.label === product?.conditionnementCampagne) {
            if (!c.centilisations?.find(c => c.value === product?.centilisation!)) {
              c.centilisations?.push({
                value: product?.centilisation!,
                idCondi: product?.conditionnementCampagne,
              });
            }
          }
        })
      }
    }
  }
  setFormat() {
    // Récupère le conditionnement sélectionné à partir du formulaire
    const condiSelected = this.conditionnements().find(c => c.label === this.cartForm.controls['conditionnement'].value);

    // Initialise les formats par défaut à un tableau vide si le conditionnement sélectionné est null ou undefined
    const formats = condiSelected?.centilisations || [];

    // Trie les formats par leur valeur en utilisant une fonction de comparaison plus performante
    formats.sort((a, b) => a.value! - b.value!); // Supprime les assertions de type inutiles

    // Crée le tableau formatsToDisplay en utilisant une fonction map plus efficace
    const formatsToDisplay = formats.map(({ value, idCondi }) => ({ value, label: `${value} L`, idCondi })); // Déstructure et utilise un littéral de modèle

    // Met à jour le tableau des formats avec les formats à afficher
    this.formats.set(formatsToDisplay);

    //Mettre à jour le champs nbBouteilles du form au changement de condi
    if(condiSelected)
      this.cartForm.controls['nbBouteilles'].setValue(condiSelected?.nbBouteilles);

    this.setQuantity();

    // Vérifie si une centilisation correspondante existe et met à jour le formulaire si nécessaire
    if (!formats.some(f => f.value === this.cartForm.controls['centilisation'].value)) {
      this.cartForm.controls['centilisation'].setValue(null);
    }
  }

  saveCart() {
    const cartData = this.createProductCart(this.product, this.cartForm, this.conditionnements());
    if (this.cartService.editMode()) {
      this.cartService.edit(this.cartForm.value, cartData.formatsByConditionnement);
    } else {
      this.cartService.add(cartData);
    }
    this.cartService.hide();
    const messageToast = this.product.nomVin + $localize`:@@bienAjouterPanier: a bien été ajoutée à votre panier. ` + '\n' + this.cartForm.controls['quantity'].value +
      ' ' + this.cartForm.controls['conditionnement'].value.toUpperCase() + ' (' +this.cartForm.controls['centilisation'].value + ' L.)';
    const saveSuccessToastMessage = new ToastMessage('success', messageToast, null, '')
    this.toastService.toastMessage$.next(saveSuccessToastMessage);
  }

  setQuantity() {
    const type = this.cartService.type();
    const centilisation = this.cartForm.controls['centilisation'].value;
    //On récupère le vrai nbBouteilles du conditionnenemnt
    const nbBtls = this.cartForm.controls['nbBouteilles'].value;
    const qty = this.cartForm.controls['quantity'].value;

    if (centilisation) {
      if (type === TypeCampagne.Primeurs) {
        const qtyEq75 = (nbBtls * centilisation / 0.75) * qty;
        this.qty.set(qtyEq75);
      } else {
        const qtyCol = (centilisation / this.product.centilisation! * nbBtls) * qty;
        this.qty.set(qtyCol);
      }
    }
  }

  createProductCart(product: CampagneProduct | Tarif | ProductCart, form: FormGroup, conditionnements: ConditionnementCart[]): ProductCart {
    const formats = conditionnements.find(c => c.label === form.controls['conditionnement'].value)?.centilisations?.sort((a, b) => a.value! - b.value!);

    const baseProductCart = {
      idVin: product.idVin,
      nomVin: product.nomVin,
      pays: product.pays,
      couleur: product.couleur,
      millesime: product.millesime,
      centilisation: product.centilisation,
      cartData: {
        conditionnement: form.controls['conditionnement'].value,
        centilisation: form.controls['centilisation'].value,
        quantity: form.controls['quantity'].value,
        total: 0
      },
      allConditionnements: conditionnements,
      formatsByConditionnement: formats?.map(f => ({
        value: f.value,
        label: f.value?.toString() + ' L',
        idCondi: f.idCondi
      }))
    };

    let prix;
    let idAppellation;
    let idCampagne;
    let conditionnementCampagne;
    let idCatalogue;
    let conditionnement;
    let nomAppellation;
    let nbBouteilles;

    if (this.isCampagne(product)) {
      prix = product.prixEq75 ? product.prixEq75 : product.prixReventeCampagne;
      idAppellation = product.idAppellation;
      idCampagne = product.idCampagne;
      conditionnementCampagne = product.conditionnementCampagne;
      nomAppellation = product.nomAppellation;
      nbBouteilles = product.nbBouteilles;
    } else if (this.isTarif(product)) {
      prix = product.prix;
      idCatalogue = product.idCatalogue;
      conditionnement = product.code_Caisse! + product.nb_Bouteille_Caisse!
      nomAppellation = product.appellation
      nbBouteilles = product.nb_Bouteille_Caisse;
    }
    const qte = new Decimal(this.qty()!);
    const prixDecimal = new Decimal(prix!);
    return {
      ...baseProductCart,
      ...(prix ? {prix} : {}),
      ...(idAppellation ? {idAppellation} : {}),
      ...(idCampagne ? {idCampagne} : {}),
      ...(conditionnementCampagne ? {conditionnementCampagne} : {}),
      ...(idCatalogue ? {idCatalogue} : {}),
      ...(conditionnement ? {conditionnement} : {}),
      ...(nomAppellation ? {nomAppellation} : {}),
      ...(nbBouteilles ? {nbBouteilles} : {}),
      cartData: {
        ...baseProductCart.cartData,
        total: qte.mul(prixDecimal).toNumber()
      },
      maxQty: this.s_max(),
      ...(this.isCampagne(product) ? { prixReventeCampagne: product.prixReventeCampagne } : {}),
    };
  }

  private isCampagne(product: any): product is CampagneProduct {
    return product && typeof product === 'object' && 'dateSortieCampagne' in product;
  }
  private isTarif(product: any): product is Tarif {
    return product && typeof product === 'object' && 'trancheQte' in product;
  }
}
